import { useEffect, type MutableRefObject } from 'react';

import { OrbitControls } from 'three-stdlib';

type UseRotationTrackingParams = {
    controls: OrbitControls;
    previousAnglesRef: MutableRefObject<{
        azimuthal: number;
        polar: number;
    }>;
    onRotate: (() => void) | undefined;
};

// Minimum angle change to trigger callback
const ANGLE_THRESHOLD = 0.01;

export const useRotationTracking = ({ controls, previousAnglesRef, onRotate }: UseRotationTrackingParams) => {
    useEffect(() => {
        if (!onRotate) return;

        const handleChange = () => {
            const currentAzimuthal = controls.getAzimuthalAngle();
            const currentPolar = controls.getPolarAngle();

            const azimuthalDiff = Math.abs(currentAzimuthal - previousAnglesRef.current.azimuthal);
            const polarDiff = Math.abs(currentPolar - previousAnglesRef.current.polar);

            // Only fire callback if angles changed (rotation), not just distance (zoom)
            if (azimuthalDiff > ANGLE_THRESHOLD || polarDiff > ANGLE_THRESHOLD) {
                onRotate();
            }

            previousAnglesRef.current = { azimuthal: currentAzimuthal, polar: currentPolar };
        };

        controls.addEventListener('change', handleChange);

        return () => controls.removeEventListener('change', handleChange);
    }, [controls, onRotate, previousAnglesRef]);
};
